import * as Sentry from '@sentry/react';
import type { Catalog } from '../store/cartSlice';
import type { CatalogItem } from '../types/catalogItem';
import type { CustomerInfo } from '../types/customerInfo';
import type { Localization } from '../types/localization';
import type { Preview } from '../types/preview';
import type { Session } from '../types/session';
import { getProductInfoByCode } from './products';

const TRIAL_QUANTITY = 1;

interface UserParams {
  session?: Session | null;
  email?: string;
  billingInfo?: Partial<CustomerInfo> | null;
}

interface OrderParams {
  transactionId?: string;
  paymentType?: string;
}

interface CartParams {
  catalog: Catalog | null;
  productsPreview: Preview | null;
  localization: Localization | null;
}

interface ItemParams {
  catalog: Catalog | null;
  localization: Localization | null;
  ratePlan: string;
  quantity: number;
  placement: string;
}

interface TrialParams {
  trialCatalog: CatalogItem | null;
  sourceCatalog: CatalogItem | null;
  localization: Localization | null;
}

const withGtmGuard =
  <A extends unknown[]>(fn: (...args: A) => void) =>
  (...args: A) => {
    try {
      return fn(...args);
    } catch (error) {
      Sentry.captureException(error);
      return undefined;
    }
  };

const required = <T>(value: T | null | undefined, name: string): T => {
  if (value === null || value === undefined) {
    throw new Error(`GTM event skipped: ${name} is not loaded`);
  }

  return value;
};

const requireCart = ({
  catalog,
  productsPreview,
  localization,
}: CartParams) => ({
  catalog,
  productsPreview: required(productsPreview, 'productsPreview'),
  localization: required(localization, 'localization'),
});

const requireTrial = ({
  trialCatalog,
  sourceCatalog,
  localization,
}: TrialParams) => ({
  trialCatalog: required(trialCatalog, 'trialCatalog'),
  sourceCatalog,
  localization: required(localization, 'localization'),
});

const pushEvent = (event: Record<string, unknown>) => {
  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push(event);
};

const TERM_TYPES: Record<string, string> = {
  Annual: 'Annual',
  Month: 'Monthly',
  Three_Years: '3 Years',
};
const LICENSE_TYPES: Record<string, string> = {
  fixed_seat: 'Named',
  floating: 'Floating',
};
const SUB_TYPES: Record<string, string> = { Student: 'Educational' };

const formatTermType = (termType: string) => TERM_TYPES[termType] ?? termType;
const formatLicenseType = (licenseType: string) =>
  LICENSE_TYPES[licenseType] ?? licenseType;
const formatSubType = (subType: string) => SUB_TYPES[subType] ?? subType;
const getBrand = (name: string) => name.trim().split(' ')[0];
const formatItemName = (name: string) => name.split(' (')[0];

const getPromoInfo = (preview: Preview) => {
  const promo = preview.promoItems?.list[0];

  return { promoName: promo?.chargeName, promoAmount: promo?.chargeAmount };
};

const buildItem = (
  catalogItem: CatalogItem,
  quantity: number,
  currencyCode: string,
  sourceProduct?: CatalogItem | null,
) => {
  const { licenseType, subType } = getProductInfoByCode(
    catalogItem.productCode,
  );
  const price =
    catalogItem.pricing.find((p) => p.currency === currencyCode)?.price || 0;

  return {
    item_id: catalogItem.ratePlanId,
    item_name: formatItemName(sourceProduct?.name || catalogItem.name),
    item_brand: getBrand(sourceProduct?.name || catalogItem.name),
    item_category: formatTermType(
      sourceProduct?.billingPeriod || catalogItem.billingPeriod,
    ),
    item_category2: formatSubType(subType),
    item_category3: formatLicenseType(
      sourceProduct?.licenseType || catalogItem.licenseType,
    ),
    item_variant: licenseType,
    currency: currencyCode,
    price,
    quantity,
  };
};

type GtmItem = ReturnType<typeof buildItem>;

const buildCartItems = (
  catalog: Catalog | null,
  productsPreview: Preview,
  localization: Localization,
) =>
  catalog?.items.map((item) => {
    const productPreview = productsPreview.invoiceItems.find(
      (preview) => preview.productRatePlanId === item.ratePlanId,
    );

    return buildItem(
      item,
      required(productPreview, `preview line for ${item.ratePlanId}`).quantity,
      localization.currencyCode,
    );
  });

const buildSingleProductItem = (
  catalog: Catalog | null,
  localization: Localization,
  ratePlan: string,
  quantity: number,
) => {
  const product = catalog?.items.find((item) => item.ratePlanId === ratePlan);

  return buildItem(
    required(product, `catalog item ${ratePlan}`),
    quantity,
    localization.currencyCode,
  );
};

const buildEcommerce = (
  productsPreview: Preview,
  localization: Localization,
  cartItems: GtmItem[] | undefined,
) => {
  const { promoName, promoAmount } = getPromoInfo(productsPreview);

  return {
    currency: localization.currencyCode,
    value: productsPreview.amountWithoutTax,
    coupon: promoName,
    discount: promoAmount,
    items: cartItems,
  };
};

const buildTrialItem = (
  trialCatalog: CatalogItem,
  sourceCatalog: CatalogItem | null,
  localization: Localization,
) =>
  buildItem(
    trialCatalog,
    TRIAL_QUANTITY,
    localization.currencyCode,
    sourceCatalog,
  );

const buildTrialEcommerce = (
  trialCatalog: CatalogItem,
  sourceCatalog: CatalogItem | null,
  localization: Localization,
) => {
  const item = buildTrialItem(trialCatalog, sourceCatalog, localization);

  return {
    currency: localization.currencyCode,
    value: item.price * TRIAL_QUANTITY,
    coupon: undefined,
    discount: undefined,
    items: [item],
  };
};

const buildUserData = (
  session: Session | null | undefined,
  email: string | undefined,
  billingInfo: Partial<CustomerInfo> | null | undefined,
) => ({
  email: session?.Email || email,
  customer_type:
    session?.Email && Object.keys(billingInfo ?? {}).length > 0
      ? 'Returning'
      : 'New',
  user_id: session?.UserGUID || undefined,
});

const pushCheckoutStepWithUser = (
  eventName: string,
  params: CartParams & UserParams,
) => {
  const { catalog, productsPreview, localization } = requireCart(params);
  const cartItems = buildCartItems(catalog, productsPreview, localization);

  pushEvent({
    event: eventName,
    user_data: buildUserData(params.session, params.email, params.billingInfo),
    ecommerce: buildEcommerce(productsPreview, localization, cartItems),
  });
};

const pushTrialStepWithUser = (
  eventName: string,
  params: TrialParams & UserParams,
) => {
  const { trialCatalog, sourceCatalog, localization } = requireTrial(params);

  pushEvent({
    event: eventName,
    user_data: buildUserData(params.session, params.email, params.billingInfo),
    ecommerce: buildTrialEcommerce(trialCatalog, sourceCatalog, localization),
  });
};

export const onInit = withGtmGuard((params: CartParams) => {
  const { catalog, productsPreview, localization } = requireCart(params);
  const cartItems = buildCartItems(catalog, productsPreview, localization);

  pushEvent({
    event: 'begin_checkout',
    ecommerce: buildEcommerce(productsPreview, localization, cartItems),
  });
});

export const onLogin = withGtmGuard((params: CartParams & UserParams) => {
  pushCheckoutStepWithUser('add_account_info', params);
});

export const onConfirmPaymentMethod = withGtmGuard(
  (params: CartParams & UserParams) => {
    pushCheckoutStepWithUser('add_payment_info', params);
  },
);

export const onRemoveProduct = withGtmGuard((params: ItemParams) => {
  const { quantity, ratePlan, placement } = params;
  const localization = required(params.localization, 'localization');
  const item = buildSingleProductItem(
    params.catalog,
    localization,
    ratePlan,
    quantity,
  );

  pushEvent({
    event: 'remove_from_cart',
    ecommerce: {
      currency: localization.currencyCode,
      value: item.price * quantity,
      placement,
      items: [item],
    },
  });
});

export const onAddToCart = withGtmGuard((params: ItemParams) => {
  const { quantity, ratePlan, placement } = params;
  const localization = required(params.localization, 'localization');
  const item = buildSingleProductItem(
    params.catalog,
    localization,
    ratePlan,
    quantity,
  );

  pushEvent({
    event: 'add_to_cart',
    ecommerce: {
      currency: localization.currencyCode,
      value: item.price * quantity,
      placement,
      items: [item],
    },
  });
});

export const onPurchase = withGtmGuard(
  (params: CartParams & UserParams & OrderParams) => {
    const { catalog, productsPreview, localization } = requireCart(params);
    const { promoName, promoAmount } = getPromoInfo(productsPreview);
    const items = buildCartItems(catalog, productsPreview, localization);

    pushEvent({
      event: 'purchase',
      user_data: buildUserData(
        params.session,
        params.email,
        params.billingInfo,
      ),
      ecommerce: {
        currency: localization.currencyCode,
        value: productsPreview.amountWithoutTax,
        transaction_id: params.transactionId,
        coupon: promoName,
        discount: promoAmount,
        tax: productsPreview.taxAmount,
        payment_type: params.paymentType,
        items,
      },
    });
  },
);

export const onTrialInit = withGtmGuard((params: TrialParams) => {
  const { trialCatalog, sourceCatalog, localization } = requireTrial(params);

  pushEvent({
    event: 'begin_checkout',
    ecommerce: buildTrialEcommerce(trialCatalog, sourceCatalog, localization),
  });
});

export const onTrialLogin = withGtmGuard((params: TrialParams & UserParams) => {
  pushTrialStepWithUser('add_account_info', params);
});

export const onTrialConfirmPaymentMethod = withGtmGuard(
  (params: TrialParams & UserParams) => {
    pushTrialStepWithUser('add_payment_info', params);
  },
);

export const onTrialPurchase = withGtmGuard(
  (params: TrialParams & UserParams & OrderParams) => {
    const { trialCatalog, sourceCatalog, localization } = requireTrial(params);
    const item = buildTrialItem(trialCatalog, sourceCatalog, localization);

    pushEvent({
      event: 'trial_submission',
      user_data: buildUserData(
        params.session,
        params.email,
        params.billingInfo,
      ),
      ecommerce: {
        currency: localization.currencyCode,
        value: item.price * TRIAL_QUANTITY,
        transaction_id: params.transactionId,
        trial_licence_id: params.transactionId,
        trial_method: params.paymentType
          ? 'With payment info'
          : 'Without payment info',
        coupon: undefined,
        tax: 0,
        payment_type: params.paymentType,
        items: [{ ...item, discount: undefined }],
      },
    });
  },
);
